// ── Conversão de cores ────────────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim())
  if (!m) return null
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l * 100]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
    case g: h = ((b - r) / d + 2) / 6; break
    case b: h = ((r - g) / d + 4) / 6; break
  }
  return [h * 360, s * 100, l * 100]
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100
  if (s === 0) {
    const v = Math.round(l * 255)
    return [v, v, v]
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue2 = (t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return [
    Math.round(hue2(h + 1 / 3) * 255),
    Math.round(hue2(h) * 255),
    Math.round(hue2(h - 1 / 3) * 255),
  ]
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')
}

// Luminância relativa (WCAG) para decidir texto branco vs preto
function relativeLuminance(r: number, g: number, b: number): number {
  const c = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b)
}

// ── Aplicação das variáveis CSS ───────────────────────────────────────────────

// Lightness‑target por shade (primary = shade 600, L≈40)
const SHADE_L: [number, number][] = [
  [50, 96], [100, 91], [200, 82], [300, 70],
  [400, 57], [500, 46], [600, 37], [700, 29],
  [800, 22], [900, 15],
]

export function applyBranding(primaryHex: string): void {
  const rgb = hexToRgb(primaryHex)
  if (!rgb) return
  const [h, s] = rgbToHsl(...rgb)
  const root = document.documentElement

  for (const [shade, targetL] of SHADE_L) {
    const [r, g, b] = hslToRgb(h, Math.min(s, 72), targetL)
    root.style.setProperty(`--brand-${shade}`, `${r} ${g} ${b}`)
  }

  // L ~37 = shade-600
  const [pr, pg, pb] = hslToRgb(h, Math.min(s, 72), 37)
  const primaryHexDerived = rgbToHex(pr, pg, pb)

  // L ~29 = shade-700 (hover dark)
  const [dr, dg, db] = hslToRgb(h, Math.min(s, 72), 29)
  const darkHex = rgbToHex(dr, dg, db)

  root.style.setProperty('--brand-primary', primaryHexDerived)
  root.style.setProperty('--brand-primary-dark', darkHex)
  root.style.setProperty(
    '--brand-on-primary',
    relativeLuminance(pr, pg, pb) > 0.179 ? '#000000' : '#ffffff'
  )
}

// Reseta para o default sage/olive definido em index.css
export function resetBranding(): void {
  const props = [
    '--brand-50', '--brand-100', '--brand-200', '--brand-300', '--brand-400',
    '--brand-500', '--brand-600', '--brand-700', '--brand-800', '--brand-900',
    '--brand-primary', '--brand-primary-dark', '--brand-on-primary',
  ]
  const root = document.documentElement
  for (const p of props) root.style.removeProperty(p)
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useBranding(_companyId: string | null) {
  // Tema FIXO "Miguel Comprador" (verde floresta, definido em index.css).
  // O override dinâmico por empresa foi desativado a pedido — a paleta é a marca
  // do produto agora. applyBranding/resetBranding ficam exportados para uso futuro
  // (ex: re-ativar white-label), mas não são chamados aqui.
  void _companyId
}
